import React, { FC, useMemo } from "react";
import { useHistory } from "react-router-dom";
import useBrandContext from "Contexts/brand-context";
import useUserContext from "Contexts/user-context";
import { SettingsCard, SettingsCardStatusType } from "Shared/components/cards";
import { useTranslation } from "Shared/util/custom-hooks";
import { BrandName } from "Shared/util/types";
import {
    FinaComputedConsentStatus,
    PayoutInformationAccount,
} from "Shared/util/types/api";
import { PayoutInformationSettingsCardItem } from "./payout-information-settings-card-item";
import styles from "./payout-information-settings-card.css";

type PayoutInformationSettingsCardProps = {
    accounts: PayoutInformationAccount[];
    className: string;
};

const hasAnyCriticalConsents = (
    accounts: PayoutInformationAccount[],
): boolean =>
    accounts?.some((x) =>
        [
            FinaComputedConsentStatus.Expired,
            FinaComputedConsentStatus.NotProvided,
        ].includes(x.consentStatus),
    );

const hasAnyWarningConsents = (accounts: PayoutInformationAccount[]): boolean =>
    accounts?.some((x) =>
        [FinaComputedConsentStatus.ExpiresSoon].includes(x.consentStatus),
    );

const getCardStatus = (
    accounts: PayoutInformationAccount[],
): SettingsCardStatusType => {
    if (!accounts?.length) {
        return "SEVERE";
    }

    const anyCriticalConsents = hasAnyCriticalConsents(accounts);
    if (anyCriticalConsents) {
        return "SEVERE";
    }

    const anyWarningConsents = hasAnyWarningConsents(accounts);
    if (anyWarningConsents) {
        return "INVALID";
    }

    return "VALID";
};

export const PayoutInformationSettingsCard: FC<
    PayoutInformationSettingsCardProps
> = ({ accounts, className }) => {
    const { brandData } = useBrandContext();
    const { user } = useUserContext();
    const history = useHistory();
    const t = useTranslation(
        (x) => x.company_settings.payoutInformationSettingsCard,
    );

    const handleCardClicked = () =>
        history.push("/company-settings/payout-information");

    const selectedPayoutAccounts = useMemo(
        () => accounts?.filter((x) => x.isPayoutAccount) ?? [],
        [accounts],
    );

    const isPayoutSettingsCardDisabled = 
        brandData?.name === BrandName.FinanceLink || 
        !user?.activeCompany.role.isAdmin;
    
    return (
        <>
            <SettingsCard
                action={{
                    disabled: isPayoutSettingsCardDisabled,
                    onClick: handleCardClicked,
                    svg: "/caret-right.svg",
                }}
                className={className || ""}
                content={
                    <div className={styles.container}>
                        {!selectedPayoutAccounts.length
                            ? t.noAccountsAdded
                            : selectedPayoutAccounts.map((x) => (
                                  <PayoutInformationSettingsCardItem
                                      key={`${x.customerPaymentMethodId}::${x.openBankingAccountId}`}
                                      payoutAccount={x}
                                  />
                              ))}
                    </div>
                }
                header={{
                    text: t.payoutInformation,
                }}
                status={{
                    text: "",
                    type: getCardStatus(accounts),
                }}
            />
        </>
    );
};
